import { ColorTypes } from "@omegagrid/core";
import { msg } from "@omegagrid/localize";
import { CellModel, ColumnSource, RequestFilters, RowModel, RowSource } from "@omegagrid/grid";
import rp, { DEFAULT_GRID_EDITOR_OPTIONS, GridEditorAdapter, GridEditorOptions, RowData } from "@reporting/rp-app-host";
import { AccountingSection } from "@reporting/rp-types/accounting";
import { GroupTreeAdapter } from "./accountingTreeAdapters";
import { ServerTreeAdapter } from "./serverTreeAdapter";

/*
* Editor of accounting number sections (P_ACCOUNTING_SECTION) only for SUPER administration!
* Sections define which accounting numbers can be assigned within a group.
*/

export class AccountingSectionGridEditorAdapter extends GridEditorAdapter {

	private _columns: ColumnSource[];

	async getColumns(): Promise<ColumnSource[]> {
		if (this._columns) return this._columns;
		this._columns = [{
			data: 'controls',
			t: 'a',
			e: false,
			w: 5 + 20,
			filter: false,
			content: [{
				action: 'delete',
				title: msg('Delete'),
				icon: 'trash',
				color: ColorTypes.Red
			}]
		}, {
			data: 'id',
			header: '#',
			t: 'n',
			z: '0',
			e: false,
			w: 60,
		}, {
			data: 'id_accounting_group',
			header: msg('Group'),
			t: 's',
			e: true,
			w: 240,
			adapter: new GroupTreeAdapter({ selectMode: 'single', autoClose: true, checkboxes: false }),
		}, {
			data: 'type',
			header: msg('Type'),
			t: 's',
			e: true,
			w: 110,
			content: [
				{ k: 'Group', v: msg('Group') },
				{ k: 'Local', v: msg('Local') },
			],
		}, {
			data: 'accounting_from',
			header: msg('From'),
			t: 'n',
			z: '0',
			e: true,
			w: 110,
		}, {
			data: 'accounting_to',
			header: msg('To'),
			t: 'n',
			z: '0',
			e: true,
			w: 110,
		}, {
			data: 'id_server',
			header: msg('Server'),
			t: 's',
			e: true,
			w: 180,
			adapter: new ServerTreeAdapter({ selectMode: 'single', autoClose: true, checkboxes: false }),
		}, {
			data: 'date_created',
			header: msg('Created'),
			t: 'dt',
			e: false,
			w: 145,
		}, {
			data: 'who_created',
			header: msg('Created by'),
			t: 't',
			e: false,
			w: 120,
		}, {
			data: 'idz',
			t: 'n',
			e: false,
			hidden: true,
		}];
		return this._columns;
	}

	async getRows(filters?: RequestFilters): Promise<RowSource[]> {
		rp.services.register('server');
		const sections = await rp.ms('accounting').get<AccountingSection[]>('sections');
		return sections.map(s => ({ data: {
			...s,
			id_accounting_group: {
				v: s.id_accounting_group?.toString() ?? null,
				dv: s.id_accounting_group != null ? `${s.id_accounting_group} ${s.group_name || ''}` : `<${msg('global')}>`,
			},
			id_server: {
				v: s.id_server?.toString() ?? null,
				dv: s.host ?? null,
			},
		}}));
	}

	async save(rowData: RowData[]) {
		const data = rowData.map(row => Object.assign({ _state: row.state }, row.data));
		// deliberately no server synchronization here, although synchronizeServers(['P_ACCOUNTING_SECTION'])
		// would merge the rows right away: the scheduled run takes sections in the 19:xx hour only and a
		// section reaching the other servers with that evening run is accepted, because whoever edits
		// the sections of a group creates its companies from the same server anyway.
		await rp.ms('accounting').post('sections', data);
	}

	getOptions(): Partial<GridEditorOptions> {
		return this.createOptions({
			colLetters: false,
			rowNumbers: true,
			columnSorting: true,
			filters: true,
			pageSize: null,
			refreshAfterSave: true,
			saveMode: 'edited_rows',
			afterRenderer: (div: HTMLDivElement, cell: CellModel) => {
				// colorize range cells when sections overlap
				if ((cell.column?.data === 'accounting_from' || cell.column?.data === 'accounting_to')) {
					const conflictRow = this.findOverlappingRow(cell.row);
					if (conflictRow?.data?.id) {
						div.style.color = "#ab0000ff";
						div.title = `${msg('Conflict with section')} #${conflictRow.data.id}`;
					}
				}
				// colorize group cell of a duplicated global row (without a group)
				if (cell.column?.data === 'id_accounting_group' && cell.value == null && this.isDuplicateGlobalRow(cell.row)) {
					div.style.color = "#ab0000ff";
				}
				return DEFAULT_GRID_EDITOR_OPTIONS.afterRenderer(div, cell);
			},
		});
	}

	private columnIndex(data: string): number {
		return this.editor?.grid?.model?.columns.findIndex(c => c.data === data) ?? -1;
	}

	private getRowRange(row: RowModel, fromIndex: number, toIndex: number): [number, number] | null {
		if (row.getProperty('deleted') || row.getProperty('empty')) return null;
		const from = row.cell(fromIndex)?.value;
		const to = row.cell(toIndex)?.value;
		if (from == null || from === '' || to == null || to === '') return null;
		const range: [number, number] = [Number(from), Number(to)];
		return isNaN(range[0]) || isNaN(range[1]) ? null : range;
	}

	private findOverlappingRow(row: RowModel): RowModel | null {
		const model = this.editor?.grid?.model;
		const fromIndex = this.columnIndex('accounting_from');
		const toIndex = this.columnIndex('accounting_to');
		if (!model || fromIndex < 0 || toIndex < 0) return null;
		const range = this.getRowRange(row, fromIndex, toIndex);
		if (!range) return null;
		for (const other of model.rows) {
			if (other === row) continue;
			const otherRange = this.getRowRange(other, fromIndex, toIndex);
			if (!otherRange) continue;
			if (range[0] <= otherRange[1] && otherRange[0] <= range[1]) return other;
		}
		return null;
	}

	private isDuplicateGlobalRow(row: RowModel): boolean {
		const model = this.editor?.grid?.model;
		const groupIndex = this.columnIndex('id_accounting_group');
		if (!model || groupIndex < 0) return false;
		if (row.getProperty('deleted') || row.getProperty('empty')) return false;
		if (row.cell(groupIndex)?.value) return false; // row itself is not global
		return model.rows.some(other => other !== row
			&& !other.getProperty('deleted') && !other.getProperty('empty') && !other.getProperty('new')
			&& !other.cell(groupIndex)?.value);
	}

}
